import React, { useState, useCallback } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import { Edit2, Eye } from 'lucide-react';
import ElementPalette from './ElementPalette';
import Canvas from './Canvas';
import ElementProperties from './ElementProperties';
import {
  createElement,
  createRowFromPreset,
  createGridWithCells
} from './utils/elementHelpers';
import {
  pathToDroppableId,
  droppableIdToPath,
  moveElementBetweenPaths,
  insertAtPath
} from './utils/pathHelpers';
import { getElementDefinition, ELEMENT_CATEGORIES } from './elementRegistry';
import './styles/ContentBuilder.css';

/**
 * ContentBuilder - Main unified page/form builder component
 *
 * @param {String} mode - 'form' or 'page'
 * @param {Array} initialElements - Initial elements to render
 * @param {Function} onChange - Callback when elements change
 * @param {Boolean} showPreview - Show preview mode toggle
 */
const ContentBuilder = ({
  mode = 'page',
  initialElements = [],
  onChange,
  showPreview = true
}) => {
  // Initialize with empty canvas (user will drag Visual Grid onto it)
  const getInitialElements = () => {
    if (initialElements && initialElements.length > 0) {
      return initialElements;
    }
    // Start with empty canvas - user drags Visual Grid onto it
    return [];
  };

  const [elements, setElements] = useState(getInitialElements());
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Update parent when elements change
  const updateElements = useCallback((newElements) => {
    setElements(newElements);
    if (onChange) {
      onChange(newElements);
    }
  }, [onChange]);

  // Handle drag end
  const handleDragEnd = useCallback((result) => {
    const { source, destination, draggableId } = result;

    // Dropped outside
    if (!destination) {
      return;
    }

    // Dropped in same place
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    // Check if dragging from palette (new element)
    const isFromPalette = draggableId.startsWith('palette-');

    if (isFromPalette) {
      // Create new element
      const elementType = draggableId.replace('palette-', '');
      const elementDef = getElementDefinition(elementType);

      // VALIDATION: Check if drop is allowed
      const destDroppableId = destination.droppableId;
      const isRootDrop = destDroppableId === 'root';
      const isGridCellDrop = destDroppableId.includes('__grid-cell-');

      // Root canvas only accepts grid-container
      if (isRootDrop && elementType !== 'grid-container') {
        console.warn(`Cannot drop ${elementType} on root canvas. Only Visual Grid allowed.`);
        return; // Reject drop
      }

      // Grid cells only accept content/form/media/action elements (not layout elements)
      if (isGridCellDrop && elementDef) {
        const allowedCategories = [
          ELEMENT_CATEGORIES.CONTENT,
          ELEMENT_CATEGORIES.MEDIA,
          ELEMENT_CATEGORIES.FORM,
          ELEMENT_CATEGORIES.ACTION,
          ELEMENT_CATEGORIES.DYNAMIC
        ];
        if (!allowedCategories.includes(elementDef.category)) {
          console.warn(`Cannot drop ${elementType} into grid cell. Only content/form elements allowed.`);
          return; // Reject drop
        }
      }

      let newElement;

      // Special handling for row - show preset picker (for now, use default 2-column)
      if (elementType === 'row') {
        const preset = { columns: [{ span: 6 }, { span: 6 }] };
        newElement = createRowFromPreset(preset);
      } else if (elementType === 'grid-container') {
        // Create grid with cells (default 2x2)
        newElement = createGridWithCells(2, 2);
      } else {
        newElement = createElement(elementType);
      }

      // Insert at destination
      const destPath = droppableIdToPath(destination.droppableId);
      const newElements = insertAtPath(
        elements,
        destPath,
        newElement,
        destination.index
      );

      updateElements(newElements);
    } else {
      // Moving existing element
      const sourcePath = droppableIdToPath(source.droppableId);
      const destPath = droppableIdToPath(destination.droppableId);

      const newElements = moveElementBetweenPaths(
        elements,
        sourcePath,
        source.index,
        destPath,
        destination.index
      );

      updateElements(newElements);
    }
  }, [elements, updateElements]);

  // Handle element selection
  const handleElementSelect = useCallback((elementId, path) => {
    setSelectedElementId(elementId);
  }, []);

  // Handle element deletion
  const handleElementDelete = useCallback((elementId, path) => {
    const deleteElement = (els) => {
      return els.filter(el => {
        if (el.id === elementId) {
          return false; // Remove this element
        }
        if (el.children) {
          el.children = deleteElement(el.children);
        }
        return true;
      });
    };

    const newElements = deleteElement([...elements]);
    updateElements(newElements);
    setSelectedElementId(null); // Deselect after delete
  }, [elements, updateElements]);

  // Handle element update
  const handleElementUpdate = useCallback((updatedElement) => {
    const updateElement = (els) => {
      return els.map(el => {
        if (el.id === updatedElement.id) {
          // Special handling for grid-container: regenerate cells if dimensions changed
          if (updatedElement.type === 'grid-container') {
            const oldRows = el.props?.rows || 2;
            const oldColumns = el.props?.columns || 2;
            const newRows = updatedElement.props?.rows || 2;
            const newColumns = updatedElement.props?.columns || 2;

            // If dimensions changed, regenerate cells
            if (oldRows !== newRows || oldColumns !== newColumns) {
              const totalCells = newRows * newColumns;
              const existingCells = el.children || [];
              const newCells = [];

              // Reuse existing cells where possible, create new ones for the rest
              for (let i = 0; i < totalCells; i++) {
                if (i < existingCells.length) {
                  // Keep existing cell
                  newCells.push(existingCells[i]);
                } else {
                  // Create new cell
                  newCells.push(createElement('grid-cell', {
                    props: {
                      rowSpan: 1,
                      columnSpan: 1,
                      padding: 'medium',
                      verticalAlign: 'top',
                      horizontalAlign: 'left'
                    }
                  }));
                }
              }

              return {
                ...updatedElement,
                children: newCells
              };
            }
          }

          return updatedElement;
        }
        if (el.children) {
          return {
            ...el,
            children: updateElement(el.children)
          };
        }
        return el;
      });
    };

    const newElements = updateElement([...elements]);
    updateElements(newElements);
  }, [elements, updateElements]);

  // Find element by ID
  const findElementById = useCallback((elementId) => {
    const findInElements = (els) => {
      for (const el of els) {
        if (el.id === elementId) {
          return el;
        }
        if (el.children) {
          const found = findInElements(el.children);
          if (found) return found;
        }
      }
      return null;
    };

    return findInElements(elements);
  }, [elements]);

  // Toggle preview mode
  const togglePreview = () => {
    setIsPreviewMode(!isPreviewMode);
    setSelectedElementId(null);
  };

  return (
    <div className="content-builder">
      <DragDropContext onDragEnd={handleDragEnd}>
        {/* Left sidebar - Element Palette */}
        {!isPreviewMode && (
          <aside className="builder-sidebar">
            <Droppable droppableId="element-palette" isDropDisabled={true}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="sidebar-droppable-wrapper"
                >
                  <ElementPalette mode={mode} />
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </aside>
        )}

        {/* Main content area */}
        <main className={`builder-main ${isPreviewMode ? 'preview-mode' : ''}`}>
          {/* Toolbar */}
          <div className="builder-toolbar">
            <div className="toolbar-left">
              <h2 className="builder-title">
                {mode === 'form' ? 'Form Designer' : 'Page Builder'}
              </h2>
              <span className="element-count">
                {countElements(elements)} element{countElements(elements) !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="toolbar-right">
              {showPreview && (
                <button
                  className={`toolbar-button ${isPreviewMode ? 'active' : ''}`}
                  onClick={togglePreview}
                >
                  {isPreviewMode ? <><Edit2 size={16} className="mr-2" /> Edit</> : <><Eye size={16} className="mr-2" /> Preview</>}
                </button>
              )}
            </div>
          </div>

          {/* Canvas */}
          <div className={`builder-canvas ${isPreviewMode ? 'preview' : 'editor'}`}>
            <Canvas
              elements={elements}
              path={[]}
              onElementSelect={handleElementSelect}
              selectedElementId={selectedElementId}
              isEditorMode={!isPreviewMode}
              onElementDelete={handleElementDelete}
            />
          </div>
        </main>

        {/* Right sidebar - Element Properties */}
        {!isPreviewMode && selectedElementId && (
          <aside className="builder-properties">
            <ElementProperties
              element={findElementById(selectedElementId)}
              onUpdate={handleElementUpdate}
              onDelete={() => handleElementDelete(selectedElementId)}
              onClose={() => setSelectedElementId(null)}
            />
          </aside>
        )}
      </DragDropContext>
    </div>
  );
};

// Helper to count elements recursively
const countElements = (elements) => {
  let count = 0;
  const traverse = (els) => {
    els.forEach(el => {
      count++;
      if (el.children) {
        traverse(el.children);
      }
    });
  };
  traverse(elements);
  return count;
};

export default ContentBuilder;
